import { motion } from "framer-motion";
import { Plus } from "lucide-react";
import { useCart, COMING_SOON } from "@/context/CartContext";
import { toast } from "sonner";

const rarityStyle = {
    "ultra-rare": "border-red-700/70 bg-red-950/50 text-red-400",
    rare: "border-amber-800/60 bg-amber-950/40 text-amber-400",
    common: "border-emerald-800/50 bg-emerald-950/40 text-emerald-400",
};

export const ProductCard = ({ product, onOpen, index = 0 }) => {
    const { add } = useCart();
    const soldOut = product.stock <= 0;

    const handleMove = (e) => {
        const r = e.currentTarget.getBoundingClientRect();
        e.currentTarget.style.setProperty("--mx", `${e.clientX - r.left}px`);
        e.currentTarget.style.setProperty("--my", `${e.clientY - r.top}px`);
    };

    return (
        <motion.article
            initial={{ opacity: 0, y: 32 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-40px" }}
            transition={{ duration: 0.6, delay: (index % 4) * 0.08 }}
            onMouseMove={handleMove}
            data-testid="product-card-item"
            className="spotlight-card group relative flex cursor-pointer flex-col border border-red-950/50 bg-coal transition-colors duration-500 hover:border-red-800/80"
            onClick={() => onOpen(product)}
        >
            <div className="relative overflow-hidden">
                <img
                    src={product.image_url}
                    alt={`${product.name} — ${product.species || product.morph}`}
                    loading="lazy"
                    className="h-64 w-full object-cover transition-transform duration-700 ease-out group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-coal via-transparent to-transparent" />
                <span
                    className={`absolute left-3 top-3 border px-2 py-1 font-mono2 text-[9px] uppercase tracking-[0.2em] backdrop-blur-sm ${rarityStyle[product.rarity] || rarityStyle.common}`}
                >
                    {product.rarity}
                </span>
                {product.category === "caresheets" ? (
                    <span className="absolute right-3 top-3 border border-red-800/60 bg-black/80 px-2 py-1 font-mono2 text-[9px] uppercase tracking-[0.2em] text-red-400">
                        Instant PDF
                    </span>
                ) : soldOut ? (
                    <span className="absolute right-3 top-3 bg-black/80 px-2 py-1 font-mono2 text-[9px] uppercase tracking-[0.2em] text-ash">
                        Sold out
                    </span>
                ) : (
                    <span className="absolute right-3 top-3 flex items-center gap-1.5 bg-black/80 px-2 py-1 font-mono2 text-[9px] uppercase tracking-[0.2em] text-emerald-400">
                        <span className="h-1 w-1 rounded-full bg-emerald-400 animate-pulse" />
                        {product.stock} in stock
                    </span>
                )}
            </div>

            <div className="flex flex-1 flex-col p-5">
                <p className="font-editorial text-sm italic text-ash">
                    {product.species || product.morph}
                </p>
                <h3 className="mt-1 font-display text-lg font-bold text-bone">{product.name}</h3>
                <div className="mt-3 flex flex-wrap gap-x-4 gap-y-1 font-mono2 text-[10px] uppercase tracking-[0.15em] text-ash/70">
                    {product.care_level && <span>Care · {product.care_level}</span>}
                    {product.humidity && <span>Humidity · {product.humidity}</span>}
                </div>
                <div className="mt-5 flex items-center justify-between border-t border-red-950/40 pt-4">
                    <span className="font-mono2 text-base text-bone">
                        ${product.price.toLocaleString()}
                    </span>
                    {COMING_SOON ? (
                        <span className="border border-red-900/60 px-3 py-2 font-mono2 text-[10px] uppercase tracking-[0.15em] text-red-500" data-testid="product-card-coming-soon">
                            Coming Soon
                        </span>
                    ) : soldOut ? (
                        <button
                            data-testid="product-card-join-waitlist"
                            onClick={(e) => {
                                e.stopPropagation();
                                onOpen(product);
                            }}
                            className="flex items-center gap-2 border border-amber-800/60 px-3 py-2 font-mono2 text-[10px] uppercase tracking-[0.15em] text-amber-400 transition-colors duration-300 hover:bg-amber-900/40"
                        >
                            Join Waitlist
                        </button>
                    ) : (
                        <button
                            data-testid="product-card-add-to-cart"
                            onClick={(e) => {
                                e.stopPropagation();
                                add(product);
                                toast.success(`${product.name} added to your enclosure`);
                            }}
                            className="flex items-center gap-2 border border-red-900/60 px-3 py-2 font-mono2 text-[10px] uppercase tracking-[0.15em] text-red-400 transition-colors duration-300 hover:bg-crimson hover:text-bone"
                        >
                            <Plus className="h-3 w-3" /> Add to Enclosure
                        </button>
                    )}
                </div>
            </div>
        </motion.article>
    );
};
